<template>
  <div :class="{ hasSelectedTags: hasSelectedTags, isOverflowing: isOverflowing }">
    <multiselect
      :ref="refKey"
      v-model="getFieldValue"
      :allow-empty="field.allowEmpty"
      :placeholder="field.placeholder"
      :tag-placeholder="field.placeholder"
      :options="field.options"
      :track-by="field.valueField"
      :label="field.selectLabel"
      :multiple="true"
      :disabled="field.disabled"
      :show-labels="false"
      :limit="maxVisibleTags"
      :name="field.name"
      :class="{ 'is-invalid': isInvalidField, 'is-valid': isValidField }"
    >
      <template v-slot:limit>
        <span v-b-tooltip.hover.html="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" class="moreTag"
          :title="getFieldValue.slice(maxVisibleTags).map(el => el[field.selectLabel]).join(', ')">
            and {{ getFieldValue.length - maxVisibleTags }} more
        </span>
      </template>
      <template v-slot:option="{ option }">
        <div class="option__desc">
          <span class="option__title" :class="field.labelClass">
            {{ field.selectLabel ? option[field.selectLabel] : option }}
          </span>
        </div>
      </template>
      <template v-slot:tag="{ option, remove }">
        <span
          class="custom-tag-class"
          ref="tagRefs"
          v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }"
          :title="field.selectLabel ? option[field.selectLabel] : option"
          :style="{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#fff',
            borderRadius: '3px',
            color: '#3d5170',
            padding: '2px 4px',
            height: '16px',
            marginRight: '2px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: getTagMaxWidth(option) + 'px'
          }"
        >
          <span
            :style="{
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: (getTagMaxWidth(option) - 20) + 'px'
            }"
            :title="field.selectLabel ? option[field.selectLabel] : option"
          >
            {{ field.selectLabel ? option[field.selectLabel] : option }}
          </span>
          <span
            style="margin-left: 2px; cursor: pointer; flex-shrink: 0;"
            @click.prevent
            @mousedown.prevent.stop="remove(option, $event)"
            class="material-icons"
          >close</span>
        </span>
      </template>
    </multiselect>
  </div>
</template>

<script>
const initialMaxVisibleTags = 99
const moreIndicatorMaxWidth = 85
const tagPadding = 8 // 2px + 4px padding + 2px margin
const closeIconWidth = 20 // approximate width of close icon

export default {
  name: 'TagsMultiselect',
  props: {
    field: {
      type: Object,
      default: () => ({})
    },
    value: {},
    validation: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      containerWidth: 0,
      tagWidths: [],
      maxVisibleTags: initialMaxVisibleTags,
      hasSelectedTags: false,
      observer: null,
      isOverflowing: false,
      tagMaxWidths: {} // for storing maximum tag widths
    }
  },
  computed: {
    refKey () {
      return this.field.name
    },
    getFieldValue: {
      get () {
        return this.value
      },
      set (newValue) {
        this.$emit('input', newValue)
        this.$emit('update', this.$vnode.key, this.field)
      }
    },
    isInvalidField () {
      return this.validation && this.validation.value && this.validation.value.$error
    },
    isValidField () {
      return this.validation && this.validation.value && this.validation.value.$dirty && !this.validation.value.$error
    }
  },
  mounted () {
    this.updateContainerWidth()
    this.observeResize()
  },
  watch: {
    'field.value': {
      handler (values) {
        this.isOverflowing = false
        this.maxVisibleTags = initialMaxVisibleTags
        this.$nextTick(() => {
          this.calculateTagLayout(values)
        })
      }
    },
    value: {
      handler (values) {
        this.isOverflowing = false
        this.maxVisibleTags = initialMaxVisibleTags
        this.$nextTick(() => {
          this.calculateTagLayout(values)
        })
      }
    },
    isOverflowing (value) {
      if (value) {
        this.$nextTick(() => {
          this.calculateTagLayout(this.value)
        })
      }
    }
  },
  methods: {
    observeResize () {
      const element = this.$refs[this.refKey]?.$el?.querySelector('.multiselect__tags')
      if (!element) return

      this.observer = new ResizeObserver(() => {
        this.maxVisibleTags = initialMaxVisibleTags
        this.updateContainerWidth()
        this.$nextTick(() => {
          this.calculateTagLayout(this.value)
        })
      })

      this.observer.observe(element)
    },
    updateContainerWidth () {
      const element = this.$refs[this.refKey]?.$el?.querySelector('.multiselect__tags')
      if (!element) return

      const computedStyle = window.getComputedStyle(element)
      const width = element.clientWidth
      const paddingLeft = parseFloat(computedStyle.paddingLeft)
      const paddingRight = parseFloat(computedStyle.paddingRight)

      this.containerWidth = width - paddingLeft - paddingRight
    },

    getTagMaxWidth (option) {
      const optionKey = this.field.selectLabel ? option[this.field.selectLabel] : option
      return this.tagMaxWidths[optionKey] || this.containerWidth
    },

    calculateTagLayout (values) {
      if (!values || values.length === 0) {
        this.hasSelectedTags = false
        this.tagMaxWidths = {}
        return
      }

      this.hasSelectedTags = true
      // Create temporary element for text measurement
      const tempDiv = document.createElement('div')
      tempDiv.style.position = 'absolute'
      tempDiv.style.visibility = 'hidden'
      tempDiv.style.whiteSpace = 'nowrap'
      tempDiv.style.font = window.getComputedStyle(document.body).font
      document.body.appendChild(tempDiv)

      let availableWidth = this.containerWidth
      let visibleTags = 0
      this.tagMaxWidths = {}
      for (let i = 0; i < values.length; i++) {
        const option = values[i]
        const text = this.field.selectLabel ? option[this.field.selectLabel] : option
        // Measure full text width
        tempDiv.textContent = text
        const fullTextWidth = tempDiv.offsetWidth + tagPadding + closeIconWidth
        if (i === 0) {
          // Always show first tag, even if it needs to be truncated
          if (fullTextWidth <= availableWidth) {
            // First tag fits completely
            this.tagMaxWidths[text] = fullTextWidth
            availableWidth -= fullTextWidth
            visibleTags = 1
          } else {
            // First tag doesn't fit completely
            if (values.length > 1) {
              // If there are more tags, leave space for "and X more"
              const maxWidthForFirstTag = this.containerWidth - moreIndicatorMaxWidth
              this.tagMaxWidths[text] = Math.max(80, maxWidthForFirstTag)
              visibleTags = 1
              this.isOverflowing = true
            } else {
              // If this is the only tag, it can take full width
              this.tagMaxWidths[text] = this.containerWidth
              visibleTags = 1
              this.isOverflowing = fullTextWidth > this.containerWidth
            }
            break
          }
        } else {
          // For other tags, check if they fit completely
          // Check if there's space for tag + "and X more" indicator (if not the last tag)
          const needSpaceForMore = i < values.length - 1
          const requiredSpace = needSpaceForMore ? fullTextWidth + moreIndicatorMaxWidth : fullTextWidth
          if (requiredSpace <= availableWidth) {
            this.tagMaxWidths[text] = fullTextWidth
            availableWidth -= fullTextWidth
            visibleTags++
          } else {
            // Tag doesn't fit - show "and X more"
            this.isOverflowing = true
            // Check if we need to reduce the last visible tag
            if (visibleTags > 0 && availableWidth < moreIndicatorMaxWidth) {
              // Reduce last visible tag to make space for "and X more"
              const lastVisibleIndex = visibleTags - 1
              const lastOption = values[lastVisibleIndex]
              const lastText = this.field.selectLabel ? lastOption[this.field.selectLabel] : lastOption
              const spaceNeeded = moreIndicatorMaxWidth - availableWidth
              this.tagMaxWidths[lastText] = Math.max(80, this.tagMaxWidths[lastText] - spaceNeeded)
            }
            break
          }
        }
      }

      // If all tags fit
      if (visibleTags === values.length) {
        this.isOverflowing = false
      }

      this.maxVisibleTags = Math.max(1, visibleTags)
      document.body.removeChild(tempDiv)
    }
  },
  beforeDestroy () {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
</script>

<style scoped>
.hasSelectedTags ::v-deep .multiselect__tags-wrap {
  display: inline-block;
  white-space: nowrap;
}

.isOverflowing ::v-deep .multiselect__tags-wrap {
  width: calc(100% - 85px);
}

.hasSelectedTags ::v-deep .multiselect__single {
  display: none;
}

.multiselect--active ::v-deep .multiselect__tags-wrap {
  visibility: hidden;
  display: none;
}

.multiselect--active ::v-deep .moreTag {
  visibility: hidden;
  display: none;
}

.multiselect--active ::v-deep .multiselect__strong {
  visibility: hidden;
  display: none;
}

::v-deep .multiselect__strong {
  margin-left: 3px;
}
</style>
